// Sugalarm — Cycle 456: "Who's treating Sam?"
//
// The sheet that asks, AFTER "I'm treating this" has already opened the
// window and quieted the alarm, which hero took it. Heroes are avatar
// tiles; a pick attributes the window that is already open.
//
// ⚠ IT GATES NOTHING. The window, the timer, the alarm and the two loud
// controls ("I need help now", "Resume alerts") are untouched by anything
// here: the sheet renders IN PLACE OF the treating screen's main area, so
// those controls stay below it, fully visible and working, by construction
// (never an overlay, never a portal — `nothingCoversAlarm.test.ts`). A
// failed request says so in the footer and changes nothing else.
//
// It closes on a pick (after a 4 s confirmation with Undo), on Skip, after
// `secondsTotal` with no touch (a server-owned constant), and — through its
// parent's `pickerMustClose` — the moment the window closes or changes, a
// prompt comes up, or any alarm audio starts on this device.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import * as attributionApi from "../../api/attribution";
import {
  pickerConfirmation,
  pickerCountdownLine,
  pickerPage,
  pickerPageCount,
  pickerPageSize,
  pickerRowsThatFit,
  pickerTitle,
  setAttributionPickerOpen,
} from "../../lib/attributionPicker";
import type { IobChipEntry } from "../../lib/iobChip";
import { useHeroRosterVersion } from "../../lib/heroRosterSignal";
import type { HeroRosterItem, TreatingBy } from "../../lib/types";
import { HeroAvatar } from "../ui/HeroAvatar";
import styles from "./WhoTreatingSheet.module.css";

export type PickerCloseReason = "picked" | "skip" | "timeout";

interface Props {
  variant: "kiosk" | "web" | "mobile";
  landscape: boolean;
  memberId: string;
  memberName: string | null;
  /** The open window's identity (`custody_opened_at`). */
  windowId: string;
  /** Seconds the sheet stays up untouched (server-owned, 45). */
  secondsTotal: number;
  /** The treatment timer, for the chip. */
  timerText: string;
  timerFraction: number;
  /** The IOB card's own entries (≤ 3); empty hides the chip. */
  iob: IobChipEntry[];
  /** The hero the window is attributed to now ("Not Maya?" reopens with it). */
  current: TreatingBy | null;
  onClose: (reason: PickerCloseReason) => void;
  /** Injectable for the harness and tests; defaults to the real API. */
  api?: Pick<typeof attributionApi, "roster" | "attribute" | "undo">;
  /** Injectable clock (ms). */
  now?: () => number;
}

const CONFIRM_MS = 4_000;
const RING = 2 * Math.PI * 15;

type Phase =
  | { kind: "choosing" }
  | { kind: "saving"; heroId: string | null }
  | { kind: "confirmed"; heroId: string | null; name: string | null }
  | { kind: "error"; heroId: string | null; message: string; retry: "attribute" | "undo" };

export function WhoTreatingSheet({
  variant,
  landscape,
  memberId,
  memberName,
  windowId,
  secondsTotal,
  timerText,
  timerFraction,
  iob,
  current,
  onClose,
  api = attributionApi,
  now = () => Date.now(),
}: Props) {
  const [heroes, setHeroes] = useState<HeroRosterItem[] | null>(null);
  const [page, setPage] = useState(0);
  const [phase, setPhase] = useState<Phase>({ kind: "choosing" });
  const [touchedAt, setTouchedAt] = useState(() => now());
  const [tick, setTick] = useState(() => now());
  const closedRef = useRef(false);

  const close = useCallback(
    (reason: PickerCloseReason) => {
      if (closedRef.current) return;
      closedRef.current = true;
      onClose(reason);
    },
    [onClose],
  );

  // The rail dims while this is up (kiosk), and undims when it goes.
  useEffect(() => {
    setAttributionPickerOpen(true);
    return () => setAttributionPickerOpen(false);
  }, []);

  // Cycle 457: a picture change anywhere in the household re-fetches the
  // roster (lib/heroRosterSignal), so a tile shows the new picture within
  // one WS round trip. A failed re-fetch keeps the tiles already shown.
  const rosterVersion = useHeroRosterVersion(memberId);
  useEffect(() => {
    let live = true;
    void api.roster(memberId).then((r) => {
      if (!live) return;
      if (r.ok && Array.isArray(r.data)) setHeroes(r.data);
      else setHeroes((prev) => prev ?? []);
    });
    return () => {
      live = false;
    };
  }, [api, memberId, rosterVersion]);

  // One clock for the countdown and the confirmation.
  useEffect(() => {
    const id = window.setInterval(() => setTick(now()), 250);
    return () => window.clearInterval(id);
  }, [now]);

  const secondsLeft = Math.max(0, secondsTotal - (tick - touchedAt) / 1000);
  useEffect(() => {
    // A save that hangs does not hold the sheet up either (astra review):
    // the window and the timer never depended on it.
    if (phase.kind !== "confirmed") {
      if (secondsLeft <= 0) close("timeout");
    }
  }, [secondsLeft, phase.kind, close]);

  // The confirmation dismisses itself after 4 s.
  const confirmedAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (phase.kind !== "confirmed") {
      confirmedAtRef.current = null;
      return;
    }
    if (confirmedAtRef.current == null) confirmedAtRef.current = now();
    if (tick - confirmedAtRef.current >= CONFIRM_MS) close("picked");
  }, [phase, tick, now, close]);

  const touch = () => setTouchedAt(now());

  // ⚠ ROWS FIT THE HEIGHT THE SHEET HAS, NEVER THE OTHER WAY ROUND. A
  // short phone cannot hold three rows of tiles under the header; rather
  // than shrink a tile, the page holds fewer rows and the pager carries
  // the rest. Measured in LAYOUT px (`offsetHeight` — never a transformed
  // rect, the cycle-400 trap). The grid area's height does not depend on
  // what is inside it (`min-height: 0`, overflow hidden), so this settles
  // in one pass. Unknown (0) keeps the full page.
  const gridWrapRef = useRef<HTMLDivElement | null>(null);
  const [gridH, setGridH] = useState(0);
  useLayoutEffect(() => {
    const el = gridWrapRef.current;
    if (!el) return;
    const read = () => setGridH(el.offsetHeight);
    read();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const cols = variant === "mobile" && !landscape ? 2 : 3;
  const fullPage = pickerPageSize(variant, landscape);
  const pageSize = pickerRowsThatFit({
    available: gridH,
    rowPx: variant === "mobile" ? (landscape ? 52 : 60) : 112,
    gapPx: variant === "mobile" ? (landscape ? 6 : 8) : 12,
    pagerPx: landscape ? 0 : 48,
    cols,
    fullPage,
    heroCount: heroes?.length ?? 0,
  });
  const pageCount = pickerPageCount(heroes?.length ?? 0, pageSize);
  // A landscape phone has no height for a pager row: its arrows flank the
  // one row of tiles instead (same 44px controls, nothing smaller).
  const flank = pageCount > 1 && variant === "mobile" && landscape;
  const visible = useMemo(
    () => pickerPage(heroes ?? [], page, pageSize),
    [heroes, page, pageSize],
  );

  const send = useCallback(
    async (heroId: string | null) => {
      setPhase({ kind: "saving", heroId });
      const r = await api.attribute(memberId, windowId, heroId);
      if (closedRef.current) return;
      if (r.ok) {
        const name =
          heroId == null
            ? null
            : heroes?.find((h) => h.id === heroId)?.display_name ?? r.data?.display_name ?? null;
        setPhase({ kind: "confirmed", heroId, name });
        return;
      }
      const status = r.error?.status;
      if (status === 409) {
        // The window this sheet was asking about has ended or changed.
        close("skip");
        return;
      }
      setPhase({
        kind: "error",
        heroId,
        retry: "attribute",
        message:
          status === 429
            ? "Too many taps. Wait a moment, then try again."
            : "Couldn't save that. The timer is still running.",
      });
    },
    [api, memberId, windowId, heroes, close],
  );

  const undo = useCallback(async () => {
    touch();
    const r = await api.undo(memberId, windowId);
    if (closedRef.current) return;
    if (r.ok) {
      setPhase({ kind: "choosing" });
      return;
    }
    if (r.error?.status === 409) {
      close("skip");
      return;
    }
    setPhase({ kind: "error", heroId: null, retry: "undo", message: "Couldn't undo that." });
  }, [api, memberId, windowId, close]); // eslint-disable-line react-hooks/exhaustive-deps

  const chosen =
    phase.kind === "saving" || phase.kind === "confirmed" ? phase.heroId : undefined;
  const busy = phase.kind === "saving";

  return (
    <section
      className={`${styles.sheet} ${styles[variant] ?? ""}${landscape ? ` ${styles.landscape}` : ""}${
        pageCount > 1 ? ` ${styles.paged}` : ""
      }`}
      data-testid="who-treating-sheet"
      data-picker-sheet
      role="dialog"
      aria-modal="false"
      aria-label={pickerTitle(memberName)}
      onPointerDown={touch}
    >
      <header className={styles.head}>
        <div className={styles.titles}>
          <h2 className={styles.title}>{pickerTitle(memberName)}</h2>
          <p className={styles.sub}>Tap your picture.</p>
        </div>
        <div className={styles.chips}>
          <div className={styles.timerChip} data-testid="picker-timer-chip">
            <svg className={styles.ringSvg} viewBox="0 0 40 40" aria-hidden>
              <circle cx="20" cy="20" r="15" className={styles.ringTrack} />
              <circle
                cx="20"
                cy="20"
                r="15"
                className={styles.timerArc}
                strokeDasharray={RING}
                strokeDashoffset={RING * (1 - Math.max(0, Math.min(1, timerFraction)))}
              />
            </svg>
            <span className={styles.chipText}>
              <span className={`${styles.chipValue} tnum`}>{timerText}</span>
              <span className={styles.chipLabel}>Timer</span>
            </span>
          </div>
          {iob.length > 0 && (
            <div className={styles.iobChip} data-testid="picker-iob-chip">
              <span className={styles.iobLabel}>IOB</span>
              {iob.slice(0, 3).map((e) => (
                <span className={styles.iobEntry} key={e.key} data-iob-kind={e.kind}>
                  <svg className={styles.iobRing} viewBox="0 0 40 40" aria-hidden>
                    <circle cx="20" cy="20" r="15" className={styles.ringTrack} />
                    <circle
                      cx="20"
                      cy="20"
                      r="15"
                      fill="none"
                      stroke={e.color}
                      strokeWidth={5}
                      strokeLinecap="round"
                      strokeDasharray={RING}
                      strokeDashoffset={RING * (1 - e.fraction)}
                      transform="rotate(-90 20 20)"
                    />
                  </svg>
                  <span className={styles.chipText}>
                    <span className={`${styles.iobAmount} tnum`}>{e.amount}</span>
                    <span className={styles.chipLabel}>{e.label}</span>
                  </span>
                </span>
              ))}
            </div>
          )}
        </div>
      </header>

      <div className={styles.gridWrap} ref={gridWrapRef}>
        <div className={styles.gridRow}>
        {flank && (
          <button
            type="button"
            className={styles.pageBtn}
            aria-label="Previous heroes"
            disabled={page <= 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            ‹
          </button>
        )}
        {heroes == null ? (
          <p className={styles.loading}>Loading heroes…</p>
        ) : heroes.length === 0 ? (
          <p className={styles.loading}>No heroes are set up yet.</p>
        ) : (
          <div className={styles.grid} data-testid="picker-grid">
            {visible.map((h) => {
              const isChosen = chosen === h.id;
              const faded = chosen !== undefined && !isChosen;
              const wasCurrent = current?.hero_id === h.id && chosen === undefined;
              return (
                <button
                  type="button"
                  key={h.id}
                  className={`${styles.tile}${isChosen ? ` ${styles.tileChosen}` : ""}${
                    faded ? ` ${styles.tileFaded}` : ""
                  }${wasCurrent ? ` ${styles.tileCurrent}` : ""}`}
                  data-testid="picker-tile"
                  disabled={busy || phase.kind === "confirmed"}
                  onClick={() => void send(h.id)}
                >
                  <HeroAvatar
                    preset={h.avatar_preset}
                    photo={h.avatar_photo ?? null}
                    name={h.display_name}
                    size={variant === "mobile" ? (landscape ? 40 : 44) : 80}
                  />
                  <span className={styles.tileName}>{h.display_name}</span>
                </button>
              );
            })}
          </div>
        )}
        {flank && (
          <button
            type="button"
            className={styles.pageBtn}
            aria-label="More heroes"
            disabled={page >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          >
            ›
          </button>
        )}
        </div>
        {pageCount > 1 && !flank && (
          <div className={styles.pager} data-testid="picker-pager">
            <button
              type="button"
              className={styles.pageBtn}
              aria-label="Previous heroes"
              disabled={page <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              ‹
            </button>
            <span className={styles.dots} aria-label={`Page ${page + 1} of ${pageCount}`}>
              {Array.from({ length: pageCount }, (_, i) => (
                <span key={i} className={`${styles.dot}${i === page ? ` ${styles.dotOn}` : ""}`} />
              ))}
            </span>
            <button
              type="button"
              className={styles.pageBtn}
              aria-label="More heroes"
              disabled={page >= pageCount - 1}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            >
              ›
            </button>
          </div>
        )}
      </div>

      <footer className={styles.foot}>
        {phase.kind === "confirmed" ? (
          <>
            <span className={styles.confirm} role="status" data-testid="picker-confirmation">
              {pickerConfirmation(phase.name)}
            </span>
            <button type="button" className={styles.footBtn} onClick={() => void undo()}>
              Undo
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className={styles.footBtn}
              data-testid="picker-someone-else"
              disabled={busy}
              onClick={() => void send(null)}
            >
              Someone else
            </button>
            {phase.kind === "error" ? (
              <span className={styles.errorLine} role="alert" data-testid="picker-error">
                {phase.message}{" "}
                <button
                  type="button"
                  className={styles.retry}
                  onClick={() =>
                    void (phase.retry === "undo" ? undo() : send(phase.heroId))
                  }
                >
                  Try again
                </button>
              </span>
            ) : (
              <span className={styles.countdown} data-testid="picker-countdown">
                {pickerCountdownLine(secondsLeft)}
              </span>
            )}
            <button
              type="button"
              className={`${styles.footBtn} ${styles.skip}`}
              data-testid="picker-skip"
              onClick={() => close("skip")}
            >
              Skip
            </button>
          </>
        )}
      </footer>
    </section>
  );
}
